import React, { useState, useEffect } from 'react'
import { Transaction, Category, Card } from '@/domain/models'
import { SaveTransactionParams } from '@/domain/usecases'
import { CATEGORY_META } from '@/presentation/constants/category-meta'
import { today } from '@/presentation/utils/format'
import { useFlags } from '@/main/feature-flags'
import { Icon } from '../icon/icon'
import { Button } from '../button/button'

type TransactionModalProps = {
  tx: Transaction | null
  cards: Card[]
  defaultDate?: string
  onClose: () => void
  onSave: (params: SaveTransactionParams) => Promise<void>
  onDelete?: (tx: Transaction) => Promise<void>
}

const EXPENSE_CATEGORIES = Object.values(Category)

export const TransactionModal: React.FC<TransactionModalProps> = ({
  tx, cards, defaultDate, onClose, onSave, onDelete,
}) => {
  const { recurring_expense: recurringExpenseEnabled } = useFlags()
  const editing = !!tx
  const [type, setType] = useState<'expense' | 'income'>(tx?.type ?? 'expense')
  const [description, setDescription] = useState(tx?.description ?? '')
  const [value, setValue] = useState(
    tx ? String(tx.installment ? (tx.originalValue ?? tx.value) : tx.value) : ''
  )
  const [date, setDate] = useState(tx?.date ?? defaultDate ?? today())
  const [category, setCategory] = useState<Category>(
    (tx?.type === 'expense' ? tx?.category : null) ?? Category.supermarket
  )

  const initialRefMonth = tx?.referenceMonth ?? defaultDate?.slice(0, 7) ?? today().slice(0, 7)
  const [referenceMonth, setReferenceMonth] = useState(initialRefMonth)
  const [differentMonth, setDifferentMonth] = useState(() => {
    if (!tx?.referenceMonth) return false
    return tx.referenceMonth !== (tx.date?.slice(0, 7) ?? '')
  })

  const [cardId, setCardId] = useState(tx?.cardId ? String(tx.cardId) : '')
  const [installments, setInstallments] = useState(tx?.installment ? String(tx.installment.total) : '')
  const [recurring, setRecurring] = useState(tx?.recurring ?? false)

  useEffect(() => {
    if (type === 'income') { setCardId(''); setInstallments(''); setRecurring(false) }
  }, [type])

  useEffect(() => {
    if (!differentMonth) setReferenceMonth(date.slice(0, 7))
  }, [date, differentMonth])

  const handleRecurringChange = (checked: boolean) => {
    setRecurring(checked)
    if (checked) setInstallments('')
  }

  const handleDifferentMonthChange = (checked: boolean) => {
    setDifferentMonth(checked)
    if (!checked) setReferenceMonth(date.slice(0, 7))
  }

  const parseValue = (raw: string): number =>
    parseFloat(raw.replace(',', '.')) || 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const numValue = parseValue(value)
    const transaction: Transaction = {
      id: tx?.id ?? 0,
      type,
      description: description.trim() || 'Sem descrição',
      value: numValue,
      originalValue: null,
      date,
      category: type === 'expense' ? category : (tx?.refund ? tx.category : null),
      cardId: type === 'expense' && cardId ? Number(cardId) : (tx?.refund ? tx.cardId : null),
      installment:
        type === 'expense' && cardId && !recurring && Number(installments) > 1
          ? { current: tx?.installment?.current ?? 1, total: Number(installments) }
          : null,
      recurring: type === 'expense' ? recurring : false,
      // Estorno não se cria pelo modal; editar um não deixa de ser estorno.
      refund: tx?.refund ?? false,
      referenceMonth: type === 'income' ? referenceMonth : undefined,
    }
    await onSave({ transaction, id: tx?.id })
  }

  const handleDelete = async () => {
    if (tx && onDelete) await onDelete(tx)
  }

  const inputCls = 'w-full px-3 py-2 text-sm border border-border-2 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-green-100 focus:border-green-500 bg-surface text-text'
  const selectCls = `${inputCls} appearance-none`

  return (
    <div
      data-testid="modal-overlay"
      className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4"
      style={{ animation: 'fadeIn 150ms ease' }}
      onMouseDown={onClose}
    >
      <div
        data-testid="modal-body"
        className="bg-surface rounded-card w-full max-w-[480px] shadow-lg relative"
        style={{ animation: 'modalPop 180ms cubic-bezier(.2,.8,.2,1)' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-[15.5px] font-semibold text-text">
            {editing ? 'Editar movimentação' : 'Nova movimentação'}
          </h2>
          <button data-testid="btn-close" onClick={onClose} className="text-text-3 hover:text-text-4 p-1">
            <Icon name="x" size={20} />
          </button>
        </div>

        {/* Form */}
        <form aria-label="form" role="form" onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Type toggle */}
          <div className="flex rounded-[8px] border border-border-2 overflow-hidden">
            <button
              type="button"
              data-testid="type-expense"
              onClick={() => setType('expense')}
              className={[
                'flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors',
                type === 'expense' ? 'active bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400' : 'text-text-2 hover:bg-surface-2',
              ].join(' ')}
            >
              <Icon name="arrowDown" size={16} strokeWidth={2.2} /> Despesa
            </button>
            <button
              type="button"
              data-testid="type-income"
              onClick={() => setType('income')}
              className={[
                'flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors',
                type === 'income' ? 'active bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400' : 'text-text-2 hover:bg-surface-2',
              ].join(' ')}
            >
              <Icon name="arrowUp" size={16} strokeWidth={2.2} /> Recebido
            </button>
          </div>

          {/* Value */}
          <label className="block">
            <span className="text-xs font-medium text-text-2 mb-1 block">Valor</span>
            <div className="flex items-center border border-border-2 rounded-[8px] focus-within:ring-2 focus-within:ring-green-100 focus-within:border-green-500">
              <span className="px-3 text-sm text-text-3 border-r border-border-2">R$</span>
              <input
                data-testid="input-value"
                type="number" step="0.01" inputMode="decimal"
                value={value} placeholder="0,00" required
                onChange={(e) => setValue(e.target.value)}
                className="flex-1 px-3 py-2 text-sm focus:outline-none rounded-r-[8px]"
              />
              <span className="px-3 text-xs text-text-3">BRL</span>
            </div>
          </label>

          {/* Description */}
          <label className="block">
            <span className="text-xs font-medium text-text-2 mb-1 block">Descrição</span>
            <input
              data-testid="input-description"
              type="text" value={description} maxLength={255}
              placeholder="Ex.: Supermercado, salário…"
              onChange={(e) => setDescription(e.target.value)}
              className={inputCls}
            />
          </label>

          {/* Date row */}
          {type === 'expense' ? (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-medium text-text-2 mb-1 block">Data</span>
                <input type="date" value={date} required onChange={(e) => setDate(e.target.value)} className={inputCls} />
              </label>
              <label className="block" data-testid="field-category">
                <span className="text-xs font-medium text-text-2 mb-1 block">Categoria</span>
                <div className="relative">
                  <select value={category} onChange={(e) => setCategory(e.target.value as Category)} className={selectCls}>
                    {EXPENSE_CATEGORIES.map((k) => (
                      <option key={k} value={k}>{CATEGORY_META[k]?.label ?? k}</option>
                    ))}
                  </select>
                  <Icon name="chevronDown" size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-3 pointer-events-none" />
                </div>
              </label>
            </div>
          ) : (
            <label className="block">
              <span className="text-xs font-medium text-text-2 mb-1 block">Data</span>
              <input type="date" value={date} required onChange={(e) => setDate(e.target.value)} className={inputCls} />
            </label>
          )}

          {/* Income-only: different month */}
          {type === 'income' && (
            <label className="flex items-center gap-2 cursor-pointer" data-testid="field-different-month">
              <input
                data-testid="checkbox-different-month"
                type="checkbox"
                checked={differentMonth}
                onChange={(e) => handleDifferentMonthChange(e.target.checked)}
                className="rounded text-green-600 focus:ring-green-500 border-border-2"
              />
              <span className="text-sm text-text-4 flex items-center gap-1.5">
                <Icon name="calendar" size={15} strokeWidth={2} />
                Essa receita é contabilizada em outro mês
              </span>
            </label>
          )}

          {type === 'income' && differentMonth && (
            <label className="block" data-testid="field-reference-month">
              <span className="text-xs font-medium text-text-2 mb-1 block">Mês de referência</span>
              <input
                data-testid="input-reference-month"
                type="month"
                value={referenceMonth}
                required
                onChange={(e) => setReferenceMonth(e.target.value)}
                className={inputCls}
              />
            </label>
          )}

          {/* Expense-only fields */}
          {type === 'expense' && (
            <>
              {/* Payment method */}
              <label className="block" data-testid="field-payment">
                <span className="text-xs font-medium text-text-2 mb-1 block">Meio de pagamento</span>
                <div className="relative">
                  <select
                    data-testid="select-card"
                    value={cardId}
                    onChange={(e) => setCardId(e.target.value)}
                    className={selectCls}
                  >
                    <option value="">À vista</option>
                    {cards.map((c) => (
                      <option key={c.id} value={String(c.id)}>{c.name}</option>
                    ))}
                  </select>
                  <Icon name="chevronDown" size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-3 pointer-events-none" />
                </div>
              </label>

              {/* Installments (card only) */}
              {cardId && (
                <label className="block">
                  <span className="text-xs font-medium text-text-2 mb-1 block">Parcelas</span>
                  <input
                    data-testid="input-installments"
                    type="number" min="1" max="48"
                    value={installments} placeholder="1"
                    disabled={recurring}
                    onChange={(e) => setInstallments(e.target.value)}
                    className={`${inputCls} disabled:opacity-50 disabled:cursor-not-allowed`}
                  />
                </label>
              )}

              {/* Recurring */}
              {recurringExpenseEnabled && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid="field-recurring"
                    type="checkbox"
                    checked={recurring}
                    onChange={(e) => handleRecurringChange(e.target.checked)}
                    className="rounded text-green-600 focus:ring-green-500 border-border-2"
                  />
                  <span className="text-sm text-text-4 flex items-center gap-1.5">
                    <Icon name="repeat" size={15} strokeWidth={2} />
                    Despesa recorrente (repete todo mês)
                  </span>
                </label>
              )}
            </>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            {editing ? (
              <Button
                data-testid="btn-delete"
                variant="danger" size="sm"
                icon="trash"
                onClick={handleDelete}
                type="button"
              >
                Excluir
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={onClose} type="button">Cancelar</Button>
              <Button variant="primary" size="sm" icon="check" type="submit">
                {editing ? 'Salvar' : 'Adicionar'}
              </Button>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes modalPop {
          from { opacity: 0; transform: translateY(10px) scale(0.98) }
          to   { opacity: 1; transform: translateY(0) scale(1) }
        }
      `}</style>
    </div>
  )
}
